
import { useEffect, useRef, useState } from "react"

import { CustomQuoteModal } from "@components/react/pricing/custom-quote-modal"
import { StickyCTA } from "@components/react/pricing/sticky-cta"
import { GuidedProductSelector } from "@components/react/pricing/guided-product-selector"
import { OptimizedSuccessPlans } from "@components/react/pricing/optimized-success-plans"
import { AppleHeroSection } from "@components/react/pricing/avepoint-hero-section"
import { AppleStyleTestimonials } from "@components/react/pricing/avepoint-style-testimonials"
import { PopularPackagesHeader } from "@components/react/pricing/popular-packages-header"
import { AppleStylePricingCards } from "@components/react/pricing/avepoint-style-pricing-cards"
import { TrustSignalsSection } from "@components/react/pricing/trust-signals-section"
import { ResponsivePlanComparison } from "@components/react/pricing/responsive-plan-comparison"

type MainProps = {
  currentSite: Page.Site;
  invalidDomains: Array<string>;
};

export default function Main({ currentSite, invalidDomains=[] }: MainProps) {
  const cardsRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  const successPlansRef = useRef<HTMLDivElement>(null)
  const parallaxRef = useRef<HTMLDivElement>(null)

  // Update the state to include selectedPlan and add consultationModalOpen
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<string | undefined>(undefined)
  const [volumePricingMessage, setVolumePricingMessage] = useState<string | undefined>(undefined)
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false)
  const [selectedTrialPlan, setSelectedTrialPlan] = useState<string | undefined>(undefined)
  const [isMobile, setIsMobile] = useState(false)
  const [showGuidedSelector, setShowGuidedSelector] = useState(false)
  const [hasShownSelector, setHasShownSelector] = useState(false)

  // Create a function to handle opening the modal with a specific plan
  const openQuoteModalWithPlan = (planName: string, planType?: string) => {
  setSelectedPlan(planName);

  if (planType === "free") {
    setVolumePricingMessage(`You've selected the <span class="font-semibold">${planName}</span> plan for your free trial.`);
    } else if (planType === "volume") {
      setVolumePricingMessage(`You're requesting <span class="font-semibold">volume pricing</span> for the <span class="font-semibold">${planName}</span> plan.`);
    }
      else {
      setVolumePricingMessage(undefined);
    }

    setIsQuoteModalOpen(true);
  };

  // Create a function to handle opening the modal for volume pricing
  const openQuoteModalForVolumePricing = (planName: string) => {
    setSelectedPlan(planName)
    setVolumePricingMessage(`I'm interested in volume pricing for the ${planName} plan.`)
    setIsQuoteModalOpen(true)
  }

  // Add a function to handle opening the trial modal with a specific plan:
  const openTrialModal = (planName?: string) => {
    setSelectedTrialPlan(planName)
    setIsTrialModalOpen(true)
  }

  // Function to scroll to packages section
  const scrollToPackages = () => {
    const el = document.getElementById("popular-packages");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // Handle plan selection from guided selector
  const handleSelectPlan = (plan: string) => {
    if (plan === "Resilience Essential" || plan === "Control Plus") {
      openTrialModal(plan)
    } else {
      openQuoteModalWithPlan(plan)
    }
  }

  // Add this helper function at the top of your component
  const formatCounterValue = (value: any, target: string, suffix: string) => {
    // Handle decimal places for specific values
    if (target === "99.9") {
      return Number.parseFloat(value).toFixed(1) + suffix
    }
    return Math.round(value) + suffix
  }

  // Add parallax effect on scroll - optimized with throttling
  useEffect(() => {
    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (parallaxRef.current) {
            const scrollY = window.scrollY
            const parallaxElements = parallaxRef.current.querySelectorAll(".parallax-element")

            parallaxElements.forEach((element, index) => {
              const speed = index % 2 === 0 ? 0.15 : 0.1
              const yPos = -(scrollY * speed)
              const htmlElement = element as HTMLElement
              htmlElement.style.transform = `translateY(${yPos}px)`
            })
          }
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  // Optimized GSAP animations with lazy loading
  useEffect(() => {
    if (typeof window !== "undefined" && window.gsap) {
      // Use IntersectionObserver for lazy loading animations
      const observerOptions = {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }

      const handleIntersection = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target

            // Cards animation
            if (target === cardsRef.current) {
              const cards = target.querySelectorAll(".pricing-card")
              if (cards.length) {
                window.gsap.fromTo(
                  cards,
                  { opacity: 0, y: 30, scale: 0.98 },
                  {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    stagger: 0.1,
                    duration: 0.6,
                    ease: "power2.out",
                  },
                )
              }
              observer.unobserve(target)
            }

            // Checkmarks animation - simplified for performance
            if (target.classList.contains("feature-check")) {
              window.gsap.fromTo(
                target,
                { scale: 0.8, opacity: 0.5 },
                {
                  scale: 1,
                  opacity: 1,
                  duration: 0.3,
                  ease: "power2.out",
                },
              )
              observer.unobserve(target)
            }

            // Compliance badges animation
            if (target.classList.contains("compliance-badge")) {
              window.gsap.fromTo(
                target,
                { y: 20, opacity: 0 },
                {
                  y: 0,
                  opacity: 1,
                  duration: 0.5,
                  ease: "power2.out",
                },
              )
              observer.unobserve(target)
            }
          }
        })
      }

      const observer = new IntersectionObserver(handleIntersection, observerOptions)

      // Observe card container
      if (cardsRef.current) {
        observer.observe(cardsRef.current)
      }

      // Observe feature checks
      document.querySelectorAll(".feature-check").forEach((check) => {
        observer.observe(check)
      })

      // Observe compliance badges
      document.querySelectorAll(".compliance-badge").forEach((badge) => {
        observer.observe(badge)
      })

      return () => {
        observer.disconnect()
      }
    }
  }, [])

  // Check if we're on mobile - with debounce to prevent excessive re-renders
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    const checkIfMobile = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 768)
      }, 100)
    }

    // Initial check
    checkIfMobile()

    // Add event listener
    window.addEventListener("resize", checkIfMobile)

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkIfMobile)
      clearTimeout(timeoutId)
    }
  }, [])

  const handleOpenQuoteModal = () => {
    setIsQuoteModalOpen(true)
  }

  const handleOpenTrialModal = () => {
    openTrialModal()
  }

  return (
    <div className="bg-[linear-gradient(180deg,_#fbfbfd,_#f5f5f7_50%,_#f2f2f7)]">

          {/* Hero Section */}
          <div ref={parallaxRef}>
            <AppleHeroSection onRequestQuote={() => setIsQuoteModalOpen(true)} onStartTrial={scrollToPackages} onFindPlan={() => setShowGuidedSelector(true)} />
          </div>

          {/* Testimonial */}
          <section  className="-mt-8 md:-mt-8">
            <div className="container mx-auto">
              <AppleStyleTestimonials />
            </div>
          </section>

          {/* Popular Packages Section */}
          <section id="popular-packages" className="w-full py-20 md:py-28">
            <div className="container px-4 md:px-6 mx-auto">
              <PopularPackagesHeader />
              <div ref={cardsRef}>
                <AppleStylePricingCards openQuoteModalWithPlan={openQuoteModalWithPlan} openTrialModal={openTrialModal} />
              </div>
            </div>
          </section>

          {/* Trust Signals Section */}
          <TrustSignalsSection />

          {/* Plan Comparison Tabs */}
          <section ref={tabsRef} className="py-20 md:py-28">
            <div className="container px-4 md:px-6">
              {/* Conditional rendering based on screen size */}
              <ResponsivePlanComparison
                openQuoteModalWithPlan={openQuoteModalWithPlan}
                openQuoteModalForVolumePricing={openQuoteModalForVolumePricing}
              />
            </div>
          </section>

          {/* Success Plans Section */}
          <section ref={successPlansRef} className="w-full py-20 md:py-28 apple-bg-primary">
            <div className="container px-4 md:px-6 mx-auto">
              <div className="space-y-5 text-center mb-20">
                <div className="inline-block px-4 py-1 bg-[#ef483d] bg-opacity-10 rounded-full text-[#ef483d] text-sm font-medium mb-2">Success Plans</div>
                <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-4xl mx-auto text-[#1d1d1f]">Then, Choose the Best Unified Success Plan </h2>
                <p className="max-w-[800px] text-[#424245] md:text-xl mt-4 mx-auto">Put your data and AI to work faster with the proper support and expertise to ensure efficient and robust data security and integrity.  </p>
              </div>
              <OptimizedSuccessPlans openQuoteModalWithPlan={openQuoteModalWithPlan} />
            </div>
          </section>

          {/* Sticky CTA */}
          <StickyCTA
            onRequestQuote={handleOpenQuoteModal}
            onStartTrial={handleOpenTrialModal}
            onFindPlan={() => setShowGuidedSelector(true)}
          />

          {/* Popup Steps Form Modals */}
          <CustomQuoteModal
            currentSite={currentSite}
            invalidDomains={invalidDomains}
            isOpen={isQuoteModalOpen}
            onClose={() => {
              setIsQuoteModalOpen(false)
              setSelectedPlan(undefined)
              setVolumePricingMessage(undefined)
            }}
            selectedPlan={selectedPlan}
            volumePricingMessage={volumePricingMessage}
          />

          {/* Guided Product Selector Modal */}
          <GuidedProductSelector
            isOpen={showGuidedSelector}
            onClose={() => setShowGuidedSelector(false)}
            onSelectPlan={handleSelectPlan}
          />

        </div>
  )
}
